/**
 * Environment Configuration
 * Handles both npm package mode (props) and standalone deployment (env vars)
 */

export interface EnvironmentConfig {
  apiBaseUrl: string;
  coreApiUrl: string;
  environment: 'development' | 'staging' | 'production';
  enablePushData: boolean;
  enableChatHistoryLogging: boolean;
}

// Environment-based URLs (for standalone deployment only — when used as web component,
// the parent app provides the URL via api-base-url attribute)
const ENVIRONMENT_URLS: Record<string, string> = {
  development: '', // Must be provided via props or VITE_API_BASE_URL env var
  staging: '',     // Must be provided via props or VITE_API_BASE_URL env var
  production: '',  // Must be provided via props or VITE_API_BASE_URL env var
};

/**
 * Get current environment from env vars or default to development
 */
const getCurrentEnvironment = (): 'development' | 'staging' | 'production' => {
  // Check Vite env vars
  if (import.meta.env?.MODE) {
    const mode = import.meta.env.MODE;
    if (mode === 'production') return 'production';
    if (mode === 'staging') return 'staging';
    return 'development';
  }
  
  // Check NODE_ENV
  if (typeof process !== 'undefined' && process.env?.NODE_ENV) {
    const nodeEnv = process.env.NODE_ENV;
    if (nodeEnv === 'production') return 'production';
    if (nodeEnv === 'staging') return 'staging';
  }
  
  // Check window location for deployed apps
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname.includes('staging')) return 'staging';
    if (hostname.includes('prod') || hostname.includes('uhc.com')) return 'production';
  }
  
  return 'development';
};

/**
 * Get API base URL from env vars (for standalone deployment)
 */
const getEnvApiBaseUrl = (): string => {
  // Check Vite env var
  if (import.meta.env?.VITE_API_BASE_URL) {
    return import.meta.env.VITE_API_BASE_URL;
  }
  
  // Check global window config (like contract-reader pattern)
  if (typeof window !== 'undefined') {
    const globalConfig = (window as any).__RATE_LOADER_CONFIG__;
    if (globalConfig?.apiBaseUrl) {
      return globalConfig.apiBaseUrl;
    }
  }
  
  // Fallback to environment-based URL
  const env = getCurrentEnvironment();
  return ENVIRONMENT_URLS[env];
};

/**
 * Get Core API base URL from env vars
 */
const getEnvCoreApiUrl = (): string => {
  if (import.meta.env?.VITE_CORE_API_URL) {
    return import.meta.env.VITE_CORE_API_URL;
  }

  if (typeof window !== 'undefined') {
    const runtimeUrl = (window as any).__RATE_LOADER_CORE_API_URL__;
    if (runtimeUrl) return runtimeUrl;
  }

  return 'https://pcore-stage.uhc.com';
};

/**
 * Default configuration (for standalone deployment)
 */
export const defaultConfig: EnvironmentConfig = {
  apiBaseUrl: getEnvApiBaseUrl(),
  coreApiUrl: getEnvCoreApiUrl(),
  environment: getCurrentEnvironment(),
  enablePushData: true,
  enableChatHistoryLogging: import.meta.env?.VITE_ENABLE_CHAT_HISTORY_LOGGING !== 'false',
};

/**
 * Configuration manager - supports both prop-based (npm package) and env-based (standalone)
 */
class ConfigurationManager {
  private config: EnvironmentConfig;
  
  constructor() {
    this.config = { ...defaultConfig };
  }
  
  /**
   * Update configuration (called when used as npm package with props)
   */
  updateConfig(updates: Partial<EnvironmentConfig>): void {
    this.config = {
      ...this.config,
      ...updates,
    };
  }
  
  /**
   * Get current configuration
   */
  getConfig(): EnvironmentConfig {
    return { ...this.config };
  }
  
  /**
   * Get API base URL
   */
  getApiBaseUrl(): string {
    return this.config.apiBaseUrl;
  }

  /**
   * Get Core API base URL
   */
  getCoreApiUrl(): string {
    return this.config.coreApiUrl;
  }
  
  /**
   * Get environment
   */
  getEnvironment(): string {
    return this.config.environment;
  }
  
  /**
   * Check if push data is enabled
   */
  isPushDataEnabled(): boolean {
    return this.config.enablePushData;
  }
}

export const configManager = new ConfigurationManager();

// Export for debugging
if (typeof window !== 'undefined') {
  (window as any).__RATE_LOADER_CONFIG_MANAGER__ = configManager;
}
